'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  id: number;
  name: string;
  position: string;
  company: string;
  image: string;
  alt: string;
  rating: number;
  testimonial: string;
  category: string;
}

interface ClientTestimonialsProps {
  className?: string;
}

const ClientTestimonials = ({ className = '' }: ClientTestimonialsProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Dr. Sarah Mwangi",
    position: "Operations Director",
    company: "East African Mining Corporation",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1817d1df2-1763300639809.png",
    alt: "Professional African woman with short natural hair in navy blazer smiling confidently in modern office",
    rating: 5,
    testimonial: "MylesDrive's safety record speaks for itself. In three years of partnership transporting precious minerals across five countries, we've experienced zero security incidents and zero cargo losses. Their transparent compliance reporting and real-time tracking give us complete peace of mind.",
    category: "Mining Operations"
  },
  {
    id: 2,
    name: "James Ochieng",
    position: "Project Manager",
    company: "Continental Infrastructure EPC",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14426c174-1767064161415.png",
    alt: "African male professional in white shirt and glasses reviewing documents in construction site office",
    rating: 5,
    testimonial: "The comprehensive insurance coverage and emergency response protocols were crucial during our Uganda hydroelectric project. When unexpected weather delayed our heavy equipment transport, MylesDrive's team coordinated alternative routes and maintained constant communication. Their safety-first approach never compromises delivery commitments.",
    category: "EPC Projects"
  },
  {
    id: 3,
    name: "Maria Santos",
    position: "Supply Chain Director",
    company: "Global Trade Solutions",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e6221361-1763296141853.png",
    alt: "Hispanic businesswoman with long dark hair in professional attire working at modern desk with laptop",
    rating: 5,
    testimonial: "Compliance excellence is non-negotiable in cross-border trade. MylesDrive's AEO certification and perfect customs audit record have streamlined our operations across East Africa. Their documentation accuracy and regulatory knowledge are unmatched in the industry.",
    category: "Cross-Border Trade"
  },
  {
    id: 4,
    name: "David Kimani",
    position: "Safety & Compliance Officer",
    company: "African Resources Group",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bbbefe5e-1763293730922.png",
    alt: "Young African man in formal business suit with confident smile in corporate office setting",
    rating: 5,
    testimonial: "As a safety professional, I'm impressed by MylesDrive's transparent incident reporting and continuous improvement culture. Their 99.8% safety score isn't just a number—it reflects genuine commitment to protecting people and cargo. The quarterly safety audits and driver training programs set industry standards.",
    category: "Safety Management"
  }];


  const handlePrevious = () => {
    setActiveIndex((prev) => prev === 0 ? testimonials.length - 1 : prev - 1);
  };

  const handleNext = () => {
    setActiveIndex((prev) => prev === testimonials.length - 1 ? 0 : prev + 1);
  };

  if (!isHydrated) {
    return (
      <section className={`py-16 lg:py-24 bg-muted ${className}`}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse space-y-8">
              <div className="h-8 bg-border rounded w-1/3 mx-auto" />
              <div className="h-64 bg-border rounded" />
            </div>
          </div>
        </div>
      </section>);

  }

  const activeTestimonial = testimonials[activeIndex];

  return (
    <section className={`py-16 lg:py-24 bg-muted ${className}`}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 lg:mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-4">
            <Icon name="ChatBubbleLeftRightIcon" size={20} className="text-accent" />
            <span className="font-body font-semibold text-sm text-accent">Client Voices</span>
          </div>
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-foreground mb-4">
            What Our Clients Say
          </h2>
          <p className="font-body text-lg text-muted-foreground">
            Trusted by leading mining operations, EPC firms, and regional traders across Africa.
          </p>
        </div>

        {/* Testimonial Card */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-card rounded-2xl border border-border overflow-hidden shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-3">
              {/* Client Image */}
              <div className="lg:col-span-1 relative h-64 lg:h-auto">
                <AppImage
                  src={activeTestimonial.image}
                  alt={activeTestimonial.alt}
                  className="w-full h-full object-cover" />

                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent lg:bg-gradient-to-r" />
                <div className="absolute bottom-6 left-6 right-6 lg:bottom-auto lg:top-6">
                  <span className="inline-block px-3 py-1 bg-accent/90 backdrop-blur-sm rounded-full text-xs font-body font-semibold text-primary">
                    {activeTestimonial.category}
                  </span>
                </div>
              </div>

              {/* Testimonial Content */}
              <div className="lg:col-span-2 p-8 lg:p-12">
                {/* Quote Icon */}
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-accent/20 to-secondary/20 flex items-center justify-center mb-6">
                  <Icon name="ChatBubbleLeftRightIcon" size={24} className="text-accent" />
                </div>

                {/* Rating */}
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(activeTestimonial.rating)].map((_, index) =>
                  <Icon key={index} name="StarIcon" size={20} className="text-accent" variant="solid" />
                  )}
                </div>

                {/* Testimonial Text */}
                <blockquote className="font-body text-lg text-foreground leading-relaxed mb-6">
                  "{activeTestimonial.testimonial}"
                </blockquote>

                {/* Client Info */}
                <div className="border-t border-border pt-6">
                  <div className="font-headline font-bold text-xl text-foreground mb-1">
                    {activeTestimonial.name}
                  </div>
                  <div className="font-body text-sm text-muted-foreground">
                    {activeTestimonial.position}
                  </div>
                  <div className="font-body text-sm font-semibold text-accent mt-1">
                    {activeTestimonial.company}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center justify-center space-x-4 mt-8">
            <button
              onClick={handlePrevious}
              className="w-12 h-12 rounded-full bg-card border border-border flex items-center justify-center hover:bg-accent hover:border-accent hover:text-primary transition-all duration-300 group"
              aria-label="Previous testimonial">

              <Icon name="ChevronLeftIcon" size={24} className="group-hover:text-primary" />
            </button>

            {/* Dots Indicator */}
            <div className="flex items-center space-x-2">
              {testimonials.map((_, index) =>
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`transition-all duration-300 rounded-full ${
                index === activeIndex ?
                'w-8 h-2 bg-gradient-to-r from-accent to-secondary' : 'w-2 h-2 bg-border hover:bg-accent/50'}`
                }
                aria-label={`Go to testimonial ${index + 1}`} />

              )}
            </div>

            <button
              onClick={handleNext}
              className="w-12 h-12 rounded-full bg-card border border-border flex items-center justify-center hover:bg-accent hover:border-accent hover:text-primary transition-all duration-300 group"
              aria-label="Next testimonial">

              <Icon name="ChevronRightIcon" size={24} className="group-hover:text-primary" />
            </button>
          </div>

          {/* Counter */}
          <div className="text-center mt-4">
            <span className="font-body text-sm text-muted-foreground">
              {activeIndex + 1} / {testimonials.length}
            </span>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="max-w-4xl mx-auto mt-12 lg:mt-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
            { value: '150+', label: 'Active Clients', icon: 'UserGroupIcon' },
            { value: '98%', label: 'Client Retention', icon: 'HeartIcon' },
            { value: '4.9/5', label: 'Average Rating', icon: 'StarIcon' },
            { value: '1000+', label: 'Testimonials', icon: 'ChatBubbleLeftRightIcon' }].
            map((stat, index) =>
            <div key={index} className="bg-card rounded-lg border border-border p-6 text-center">
                <Icon name={stat.icon as any} size={32} className="text-accent mx-auto mb-3" />
                <div className="font-headline font-bold text-2xl text-foreground mb-1">
                  {stat.value}
                </div>
                <div className="font-body text-xs text-muted-foreground">{stat.label}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

};

export default ClientTestimonials;